/**
 * Album specific constants.
 */

/**
 * Album types.
 *
 * @type {{CONSUMER: string, EVENT: string, PRO: string, CAMP: string}}
 */
const ALBUM_TYPE = {
  CONSUMER: 'CONSUMER',
  EVENT: 'EVENT',
  PRO: 'PRO',
  CAMP: 'CAMP',
  SHUTTERBUG: 'SHUTTERBUG',
};

/**
 * Album Subtypes.
 *
 * @enum ALBUM_SUBTYPE
 * @type {{RESORTS: string, SCHOOLS: string, YOUTH_SPORTS: string, CAMPS: string, EVENTS: string, GREEK: string, SPORTS: string, CHURCH: string}}
 */
const ALBUM_SUBTYPE = {
  RESORTS: 'RESORTS',
  SCHOOLS: 'SCHOOLS',
  YOUTH_SPORTS: 'YOUTH_SPORTS',
  CAMPS: 'CAMPS',
  EVENTS: 'EVENTS',
  WEDDINGS: 'WEDDINGS',
  GREEK: 'GREEK',
  SPORTS: 'SPORTS',
  CHURCH: 'CHURCH',
  ANNIVERSARIES: 'ANNIVERSARIES',
  BIRTHDAYS: 'BIRTHDAYS',
  FUNDRAISERS: 'FUNDRAISERS',
  GRADUATIONS: 'GRADUATIONS',
  MEMORIALS: 'MEMORIALS',
  VACATIONS: 'VACATIONS',
  CUSTOM_EVENT: null,
};

const ALBUM_SORT_OPTIONS = {
  CREATED_AT: 'CREATED_AT',
  UPDATED_AT: 'UPDATED_AT',
  LAST_UPLOADED_AT: 'LAST_UPLOADED_AT',
  LAST_IDENTITY_JOINED: 'LAST_IDENTITY_JOINED',
};

const ALBUM_DATE_STATUS = {
  PRESENT: 'Present',
  FUTURE: 'Future',
  PAST: 'Past',
  NA: 'Not Provided',
};

const ALBUM_PHOTO_FILTERS = {
  ALL: 'ALL',
  PUBLISHED: 'PUBLISHED',
  HIDDEN: 'HIDDEN',
  REVIEWING: 'REVIEWING',
  REPORT_REVIEW: 'REPORT_REVIEW',
  DELETED: 'DELETED',
  DNP: 'DNP',
};

const ALBUM_PHOTO_FILTERS_LABEL = {
  [ALBUM_PHOTO_FILTERS.ALL]: 'All',
  [ALBUM_PHOTO_FILTERS.PUBLISHED]: 'Published',
  [ALBUM_PHOTO_FILTERS.HIDDEN]: 'Hidden',
  [ALBUM_PHOTO_FILTERS.REVIEWING]: 'Review',
  [ALBUM_PHOTO_FILTERS.REPORT_REVIEW]: 'Reported',
  [ALBUM_PHOTO_FILTERS.DNP]: 'DNP',
};

const ALBUM_PHOTO_STATUS = {
  PUBLISHED: 'PUBLISHED',
  UNPUBLISHED: 'UNPUBLISHED',
  REVIEWING: 'REVIEWING',
  REPORT_REVIEW: 'REPORT_REVIEW',
  AWAITING_DNP_CLEARANCE: 'AWAITING_DNP_CLEARANCE',
  MACHINE_BLOCKED: 'MACHINE_BLOCKED',
};

const ALBUM_PHOTO_STATUS_LABEL = {
  [ALBUM_PHOTO_STATUS.PUBLISHED]: 'PUBLISHED',
  [ALBUM_PHOTO_STATUS.UNPUBLISHED]: 'HIDDEN',
  [ALBUM_PHOTO_STATUS.REVIEWING]: 'REQUIRE REVIEW',
  [ALBUM_PHOTO_STATUS.REPORT_REVIEW]: 'REPORTED',
  [ALBUM_PHOTO_STATUS.AWAITING_DNP_CLEARANCE]: 'DNP',
  [ALBUM_PHOTO_STATUS.MACHINE_BLOCKED]: 'DNP',
};

const ALBUM_PHOTO_STATUS_BADGE_STYLE = {
  [ALBUM_PHOTO_STATUS.PUBLISHED]: 'success',
  [ALBUM_PHOTO_STATUS.UNPUBLISHED]: 'blue-mid',
  [ALBUM_PHOTO_STATUS.REVIEWING]: 'danger',
  [ALBUM_PHOTO_STATUS.REPORT_REVIEW]: 'yellow',
  [ALBUM_PHOTO_STATUS.AWAITING_DNP_CLEARANCE]: 'yellow',
  [ALBUM_PHOTO_STATUS.MACHINE_BLOCKED]: 'yellow',
};

const WEDDING_CREATOR_ENUM = {
  PHOTOGRAPHER: 'PHOTOGRAPHER',
  FRIEND: 'FRIEND',
  BETROTHED: 'BETROTHED',
};

const HIERARCHY_ITEM_TYPE = {
  ALBUM_FOLDER: 'ALBUM_FOLDER',
  ALBUM: 'ALBUM',
};

/**
 * Album Pricing requirements.
 *
 * @enum ALBUM_PRICING
 * @type {{MIN_PHOTO_PRICE: string, MIN_SUBSCRIPTION_PRICE: string}}
 */
const ALBUM_PRICING = {
  MIN_PHOTO_PRICE: 399, // $3.99
  MIN_SUBSCRIPTION_PRICE: 2000, // $20.00
  MIN_WEB_GALLERY_HIRES_DL_PRICE: 99, // $0.99
};

/**
 * @enum ALBUM_SPORTS_GAME_VENUE
 * @type {{HOME: string, AWAY: string}}
 */
const ALBUM_SPORTS_GAME_VENUE = {
  HOME: 'HOME',
  AWAY: 'AWAY',
};

/**
 * @enum ALBUM_HIGHLIGHTS_FILTER
 * @type {{ALL: string, FAVORITED: string}}
 */
const ALBUM_HIGHLIGHTS_FILTER = {
  ALL: 'ALL',
  FAVORITED: 'FAVORITED',
};

/**
 * @enum ALBUM_FAVORITES_SORT
 * @type {{FAVORITES_COUNT: string, CREATED_AT: string}}
 */
const ALBUM_FAVORITES_SORT = {
  FAVORITES_COUNT: 'FAVORITES_COUNT',
  // TODO: Once we update the DB, re-test this to determine whether or not
  // it is a good idea to keep it.
  // ALBUM_PHOTO_CREATED_AT: 'ALBUM_PHOTO_CREATED_AT',
};

/**
 * @enum ALBUM_FAVORITES_SORT_NAMES
 * @type {{FAVORITES_COUNT: string, ALBUM_PHOTO_CREATED_AT: string}}
 */
const ALBUM_FAVORITES_SORT_NAMES = {
  FAVORITES_COUNT: '# of Favorites',
  ALBUM_PHOTO_CREATED_AT: 'Photo Added',
};

/**
 * @enum ALBUM_PRICE_MODE
 * @type {{PPA_AND_PPD: string, PPA_ONLY: string, PPD_ONLY: string, NONE: string}}
 */
const ALBUM_PRICE_MODE = {
  PPA_AND_PPD: 'PPA_AND_PPD',
  PPA_ONLY: 'PPA_ONLY',
  PPD_ONLY: 'PPD_ONLY',
  NONE: 'NONE',
};

/**
 * @enum ALBUM_MEMBERS_SORT
 * @type {{MATCHED_PHOTOS_COUNT: string, LAST_DELIVERED_MATCHED_PHOTO: string, MATCHED_PHOTO_NOTIFICATIONS_COUNT: string}}
 */
const ALBUM_MEMBERS_SORT = {
  MATCHED_PHOTOS_COUNT: 'MATCHED_PHOTOS_COUNT',
  LAST_DELIVERED_MATCHED_PHOTO: 'LAST_DELIVERED_MATCHED_PHOTO',
  MATCHED_PHOTO_NOTIFICATIONS_COUNT: 'MATCHED_PHOTO_NOTIFICATIONS_COUNT',
  IDENTITY_LAST_NAME: 'IDENTITY_LAST_NAME',
};

/**
 * @enum ALBUM_MEMBERS_SORT
 * @type {{MATCHED_PHOTOS_COUNT: string, LAST_DELIVERED_MATCHED_PHOTO: string, MATCHED_PHOTO_NOTIFICATIONS_COUNT: string}}
 */
const ALBUM_MEMBERS_SORT_LABEL = {
  MATCHED_PHOTOS_COUNT: 'Matched Photos',
  LAST_DELIVERED_MATCHED_PHOTO: 'Last Photo Delivery',
  MATCHED_PHOTO_NOTIFICATIONS_COUNT: 'Total Alerts',
  IDENTITY_LAST_NAME: 'Last Name',
};

/**
 * @enum ALBUM_MEMBERS_CLAIMED_FILTER
 * @type {{ ALL: string, CLAIMED: string, UNCLAIMED: string}}
 */
const ALBUM_MEMBERS_CLAIMED_FILTER = {
  ALL: 'ALL',
  CLAIMED: 'CLAIMED',
  UNCLAIMED: 'UNCLAIMED',
};

/**
 * @enum ALBUM_PRIVACY
 * @type {{PUBLIC: string, PRIVATE: string}}
 */
const ALBUM_PRIVACY = {
  PRIVATE: 'PRIVATE',
  PUBLIC: 'PUBLIC',
};

/**
 * @enum ALBUM_PRIVACY_LABEL
 * @type {{PUBLIC: string, PRIVATE: string}}
 */
const ALBUM_PRIVACY_LABEL = {
  PRIVATE: 'Private',
  PUBLIC: 'Public',
};

/**
 * @enum ALBUM_MATCHING_TYPE
 * @type {{FACE_ONLY: string, FACE_AND_JERSEY: string}}
 */
const ALBUM_MATCHING_TYPE = {
  FACE_ONLY: 'FACE_ONLY',
  FACE_AND_JERSEY: 'FACE_AND_JERSEY',
};

/**
 * @enum ALBUM_MATCHING_TYPE_LABEL
 * @type {{FACE_ONLY: string, FACE_AND_JERSEY: string}}
 */
const ALBUM_MATCHING_TYPE_LABEL = {
  FACE_ONLY: 'Face Matching',
  FACE_AND_JERSEY: 'Face & Jersey Matching',
};

/**
 * @enum ALBUM_UPLOAD_PERMISSIONS
 * @type {{ANYONE: string, INVITEES: string, NONE: string, }}
 */
const ALBUM_UPLOAD_PERMISSIONS = {
  ANYONE: 'ANYONE',
  INVITEES: 'INVITEES',
  NONE: 'NONE',
};

/**
 * @enum ALBUM_UPLOAD_PERMISSIONS_LABEL
 * @type {{ANYONE: string, INVITEES: string }}
 */
const ALBUM_UPLOAD_PERMISSIONS_LABEL = {
  ANYONE: 'Anyone with access can add photos and videos',
  INVITEES: 'Only invited people are allowed to upload photos and videos',
  NONE: 'Only you can add photos and videos',
};
/**
 * @enum ALBUM_UPLOAD_PERMISSIONS_LABEL_SM
 * @type {{ANYONE: string, INVITEES: string }}
 */
const ALBUM_UPLOAD_PERMISSIONS_LABEL_SM = {
  ANYONE: 'Anyone',
  INVITEES: 'Only those I allow',
};

const ALBUM_CREATE_STEPS = {
  ACCOUNT: 'ACCOUNT',
  GENERAL: 'GENERAL',
  GRADE_CONFIGURATION: 'GRADE_CONFIGURATION',
  GREEN_SCREEN_CONFIGURATION: 'GREEN_SCREEN_CONFIGURATION',
  GALLERY_SELECTION: 'GALLERY_SELECTION',
  LOCATION_SELECTION: 'LOCATION_SELECTION',
  PHOTO_SHARING_MODE: 'PHOTO_SHARING_MODE',
  TAG_INCLUSION: 'TAG_INCLUSION',
  TAG_SELECTION: 'TAG_SELECTION',
  TAG_CONFIG: 'TAG_CONFIG',
  PHOTO_DELIVERY: 'PHOTO_DELIVERY',
  ALBUM_PRICE: 'ALBUM_PRICE',
  DATES: 'DATES',
  DISCOVERY: 'DISCOVERY',
  VIEWING_PERMISSIONS: 'VIEWING_PERMISSIONS',
  UPLOAD_PERMISSIONS: 'UPLOAD_PERMISSIONS',
  JOIN_CODE: 'JOIN_CODE',
  BRANDING: 'BRANDING',
  REVIEW: 'REVIEW',
};

/**
 * @enum ALBUM_STATUS
 * @type {{ACTIVE: string, DRAFT: string }}
 */
const ALBUM_STATUS = {
  ACTIVE: 'ACTIVE',
  DRAFT: 'DRAFT',
};

const SHUTTERBUG_CREATE_STEPS = {
  DETAILS: 'DETAILS',
  MATCHING_TYPE: 'MATCHING_TYPE',
  MATCHING: 'MATCHING',
  PERMISSIONS: 'PERMISSIONS',
  REVIEW: 'REVIEW',
};

const YEARBOOK_ALBUM_CREATE_STEPS = {
  ALBUMS: 'ALBUMS',
  REVIEW: 'REVIEW',
};

const ALBUM_DELIVERY_MODES = {
  FREE_WEB_GALLERY: 'FREE_WEB_GALLERY',
  PHOTO_FINDER: 'PHOTO_FINDER',
  SHUTTERBUG: 'SHUTTERBUG',
  INTERNAL: 'INTERNAL',
  PPA: 'PPA',
  SELL_PHOTOS: 'SELL_PHOTOS',
};

const ALBUM_VIDEO_STATUS = {
  PUBLISHED: 'PUBLISHED',
  UNPUBLISHED: 'UNPUBLISHED',
  REVIEWING: 'REVIEWING',
};

const FILE_COMMENT_PRIVACY = {
  PUBLIC: 'PUBLIC',
  PRIVATE: 'PRIVATE',
};

const ALBUM_SLIDESHOW_STATUS = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
};

const ALBUM_SLIDESHOW_MODE = {
  ALL_PHOTOS: 'ALL_PHOTOS',
  SELECTED_PHOTOS: 'SELECTED_PHOTOS',
  AUTO_LAYOUTS: 'AUTO_LAYOUTS',
  SELECTED_PHOTO_LAYOUTS: 'SELECTED_PHOTO_LAYOUTS',
  ALL_VIDEOS: 'ALL_VIDEOS',
};

const ALBUM_SLIDESHOW_CURATE_ACTIONS = {
  ADD: 'ADD',
  REMOVE: 'REMOVE',
};

const ALBUM_SLIDESHOW_PHOTOS_SOURCE = {
  ALBUM_PHOTOS: 'ALBUM_PHOTOS',
  FACE_HIGHLIGHTS: 'FACE_HIGHLIGHTS',
  OWNED_FAVORITES: 'OWNED_FAVORITES',
  MEMBERS_FAVORITES: 'MEMBERS_FAVORITES',
};

const ALBUM_SLIDESHOW_PHOTOS_SOURCE_LABEL = {
  ALBUM_PHOTOS: 'All photos',
  FACE_HIGHLIGHTS: 'Spotlight photos',
  OWNED_FAVORITES: 'My favorites',
  MEMBERS_FAVORITES: 'Member favorites',
  CUSTOM_PHOTO_SELECTION: 'Custom photo selection',
};

const ALBUM_SLIDESHOW_VIDEO_LAYOUT = {
  LANDSCAPE: 'LANDSCAPE',
  PORTRAIT: 'PORTRAIT',
};

const ALBUM_SUBTYPE_WALDOFIED_LABEL = {
  [ALBUM_SUBTYPE.RESORTS]: 'Resort',
  [ALBUM_SUBTYPE.SCHOOLS]: 'School',
  [ALBUM_SUBTYPE.YOUTH_SPORTS]: 'Event',
  [ALBUM_SUBTYPE.CAMPS]: 'Camp',
  [ALBUM_SUBTYPE.EVENTS]: 'Event',
  [ALBUM_SUBTYPE.WEDDINGS]: 'Wedding day',
  [ALBUM_SUBTYPE.GREEK]: 'Event',
  [ALBUM_SUBTYPE.SPORTS]: 'Event',
  [ALBUM_SUBTYPE.CHURCH]: 'Event',
  [ALBUM_SUBTYPE.ANNIVERSARIES]: 'Anniversary',
  [ALBUM_SUBTYPE.BIRTHDAYS]: 'Birthday',
  [ALBUM_SUBTYPE.FUNDRAISERS]: 'Fundraiser',
  [ALBUM_SUBTYPE.GRADUATIONS]: 'Graduation',
  [ALBUM_SUBTYPE.MEMORIALS]: 'Memorial',
  [ALBUM_SUBTYPE.VACATIONS]: 'Vacation',
  [ALBUM_SUBTYPE.CUSTOM_EVENT]: '',
};

const ALBUM_SUBTYPE_LABEL = {
  [ALBUM_SUBTYPE.RESORTS]: 'Resort',
  [ALBUM_SUBTYPE.SCHOOLS]: 'School',
  [ALBUM_SUBTYPE.YOUTH_SPORTS]: 'Event',
  [ALBUM_SUBTYPE.CAMPS]: 'Camp',
  [ALBUM_SUBTYPE.EVENTS]: 'Event',
  [ALBUM_SUBTYPE.WEDDINGS]: 'Wedding',
  [ALBUM_SUBTYPE.GREEK]: 'Event',
  [ALBUM_SUBTYPE.SPORTS]: 'Event',
  [ALBUM_SUBTYPE.CHURCH]: 'Event',
  [ALBUM_SUBTYPE.ANNIVERSARIES]: 'Anniversary',
  [ALBUM_SUBTYPE.BIRTHDAYS]: 'Birthday',
  [ALBUM_SUBTYPE.FUNDRAISERS]: 'Fundraiser',
  [ALBUM_SUBTYPE.GRADUATIONS]: 'Graduation',
  [ALBUM_SUBTYPE.MEMORIALS]: 'Memorial',
  [ALBUM_SUBTYPE.VACATIONS]: 'Vacation',
  [ALBUM_SUBTYPE.CUSTOM_EVENT]: 'Event',
};

export {
  ALBUM_TYPE,
  ALBUM_SUBTYPE,
  ALBUM_SORT_OPTIONS,
  ALBUM_DATE_STATUS,
  ALBUM_PHOTO_FILTERS,
  ALBUM_PHOTO_STATUS,
  ALBUM_PHOTO_STATUS_LABEL,
  ALBUM_PHOTO_STATUS_BADGE_STYLE,
  WEDDING_CREATOR_ENUM,
  HIERARCHY_ITEM_TYPE,
  ALBUM_PRICING,
  ALBUM_SPORTS_GAME_VENUE,
  ALBUM_HIGHLIGHTS_FILTER,
  ALBUM_FAVORITES_SORT,
  ALBUM_FAVORITES_SORT_NAMES,
  ALBUM_PRICE_MODE,
  ALBUM_MEMBERS_SORT,
  ALBUM_MEMBERS_CLAIMED_FILTER,
  ALBUM_PRIVACY,
  ALBUM_UPLOAD_PERMISSIONS,
  ALBUM_MATCHING_TYPE,
  ALBUM_PRIVACY_LABEL,
  ALBUM_MATCHING_TYPE_LABEL,
  ALBUM_UPLOAD_PERMISSIONS_LABEL,
  ALBUM_UPLOAD_PERMISSIONS_LABEL_SM,
  ALBUM_PHOTO_FILTERS_LABEL,
  ALBUM_MEMBERS_SORT_LABEL,
  ALBUM_CREATE_STEPS,
  YEARBOOK_ALBUM_CREATE_STEPS,
  ALBUM_STATUS,
  SHUTTERBUG_CREATE_STEPS,
  ALBUM_DELIVERY_MODES,
  ALBUM_VIDEO_STATUS,
  FILE_COMMENT_PRIVACY,
  ALBUM_SLIDESHOW_STATUS,
  ALBUM_SLIDESHOW_MODE,
  ALBUM_SLIDESHOW_CURATE_ACTIONS,
  ALBUM_SLIDESHOW_PHOTOS_SOURCE,
  ALBUM_SLIDESHOW_PHOTOS_SOURCE_LABEL,
  ALBUM_SLIDESHOW_VIDEO_LAYOUT,
  ALBUM_SUBTYPE_WALDOFIED_LABEL,
  ALBUM_SUBTYPE_LABEL,
};
